import { useCallback, useState } from 'react';
import AgentCard from '../AgentCard';
import { FakeAgentType, FakeAgentTypeKey } from '../aiAgentsConsts';
import CreateNewAgentCard from '../CreateNewAgentCard';
import { useUserSupportAgent } from './useUserSupportAgent';
import AppsConnectionDialog from '../../pages/ai-assistant/apps/AppsConnectionDialog';
import { useAiAssistant } from '../../pages/ai-assistant/useAiAssistant';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiAssistantConversations } from '../../../reducers/aiSupportReducer';
import groupBy from 'lodash/groupBy';
import { ConversationStatus } from '../../pages/ai-assistant/types';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { setActiveFakeAgent } from '../agentsHomeReducer';

type CardSection = 'launch' | 'chat';

const agent = {
  id: 'user-support-agent',
  name: 'User Support Agent',
  type: FakeAgentType.SupportAgent,
  crmOrgId: '123',
  files: [],
  updatedAt: '',
  contextComponents: [],
  facts: [],
} as any;

export const UserSupportAgentCard = ({
  section,
  onCreateConnection,
}: {
  section: CardSection;
  onCreateConnection?: () => void;
}) => {
  const [isAiAssistantDialogOpen, setAiAssistantDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { supportAgentInAgenticCenter } = useFeatureToggle();
  const { isLaunchedAgent, isLoading } = useUserSupportAgent(crmOrgId ?? '');
  const { fetchAiAssistentConversations } = useAiAssistant();
  const conversations = useSelector(selectAiAssistantConversations(crmOrgId ?? ''));
  const groupedConversations = groupBy(conversations, 'status');
  const openConversationsCount = groupedConversations[ConversationStatus.Open]?.length ?? 0;
  const resolvedConversationsCount =
    groupedConversations[ConversationStatus.Closed_By_Admin]?.length ?? 0;

  const facts = [
    { key: FakeAgentTypeKey.OPEN_CONVERSATIONS, value: openConversationsCount },
    { key: FakeAgentTypeKey.RESOLVED_CONVERSATIONS, value: resolvedConversationsCount },
  ];

  const _agent = { ...agent, facts };

  const openUserSupportPage = useCallback(() => {
    dispatch(setActiveFakeAgent({ agentType: FakeAgentType.SupportAgent }));
  }, [dispatch]);

  if (isLoading || !supportAgentInAgenticCenter) return <></>;

  const renderPageContent = () => {
    if (section === 'launch') {
      if (isLaunchedAgent) return null;

      return (
        <CreateNewAgentCard
          agentType={FakeAgentType.SupportAgent}
          onClick={() => {
            setAiAssistantDialogOpen(true);
          }}
        />
      );
    }

    if (!isLaunchedAgent) return null;

    return (
      <AgentCard
        key="user-support-agent"
        agent={_agent}
        onClick={openUserSupportPage}
        ctaText="Explore"
        hideActionButtons
      />
    );
  };

  return (
    <>
      {isAiAssistantDialogOpen && (
        <AppsConnectionDialog
          checkForManagePackage
          closeDialog={(e, isRefresh) => {
            e.stopPropagation();
            onCreateConnection?.();
            setAiAssistantDialogOpen(false);
            if (isRefresh) {
              fetchAiAssistentConversations(crmOrgId ?? '');
              openUserSupportPage();
            }
          }}
        />
      )}
      {renderPageContent()}
    </>
  );
};
